import React, { memo, useCallback, useMemo, useState, useEffect } from 'react';
import { Button } from 'components/ui/button';
import { Company, User, UserType } from '@app/models';
import { getCompanyByBin, getUserByIin } from '@app/api';
import { fullName } from '@app/helpers';
import { useUserContext } from '@app/providers';

export interface Step1Values {
  bin: string;
  company: Company | null;
  user: User | null;
  partnerFound: boolean;
  errorType: 'self' | 'not_found' | null;
}

interface Props {
  onSubmit: (company: Company | null, user: User | null) => void;
  savedValues?: Partial<Step1Values>;
  setValues?: (values: Step1Values) => void;
}

const BIN_IIN_LENGTH = 12;

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '').slice(0, BIN_IIN_LENGTH);
}

function Step1(props: Props) {
  const { onSubmit, savedValues, setValues } = props;
  const { user: authUser, company: authCompany } = useUserContext();
  const [pending, setPending] = useState<boolean>(false);
  const [bin, setBin] = useState<string>(savedValues?.bin ?? '');
  const [company, setCompany] = useState<Company | null>(
    savedValues?.company ?? null
  );
  const [user, setUser] = useState<User | null>(savedValues?.user ?? null);
  /** После успешного «Найти» показываем только «Продолжить», пока поле не меняли */
  const [partnerFound, setPartnerFound] = useState<boolean>(
    savedValues?.partnerFound ?? false
  );
  const [errorType, setErrorType] = useState<'self' | 'not_found' | null>(
    savedValues?.errorType ?? null
  );

  useEffect(() => {
    if (setValues) {
      setValues({
        bin,
        company,
        user,
        partnerFound,
        errorType,
      });
    }
  }, [bin, company, user, partnerFound, errorType, setValues]);

  const onChangeBin = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = digitsOnly(e.target.value);
    setBin(next);
    setCompany(null);
    setUser(null);
    setPartnerFound(false);
    setErrorType(null);
  }, []);

  const onClickFind = useCallback(async () => {
    const digits = digitsOnly(bin);
    if (digits.length !== BIN_IIN_LENGTH) {
      return;
    }

    try {
      setPending(true);
      setErrorType(null);

      let companyResponse: Company | null = null;
      let userResponse: User | null = null;

      try {
        companyResponse = await getCompanyByBin(digits);
      } catch {
        companyResponse = null;
      }
      try {
        userResponse = await getUserByIin(digits);
      } catch {
        userResponse = null;
      }

      const authBin = authCompany?.bin ? digitsOnly(authCompany.bin) : '';
      const authIin = authUser?.iin ? digitsOnly(authUser.iin) : '';

      const isSelfCompany =
        !!companyResponse &&
        authUser.type === UserType.ENTITY &&
        !!authCompany &&
        (companyResponse.id === authCompany.id || digits === authBin);

      const isSelfUser =
        !!userResponse &&
        (userResponse.id === authUser.id ||
          (authIin.length > 0 && digits === authIin));

      if (isSelfCompany || isSelfUser) {
        setCompany(null);
        setUser(null);
        setPartnerFound(false);
        setErrorType('self');
        return;
      }

      if (companyResponse) {
        setCompany(companyResponse);
        setUser(null);
        setPartnerFound(true);
        return;
      }

      if (userResponse) {
        setCompany(null);
        setUser(userResponse);
        setPartnerFound(true);
        return;
      }

      setCompany(null);
      setUser(null);
      setPartnerFound(false);
      setErrorType('not_found');
    } finally {
      setPending(false);
    }
  }, [authCompany, authUser, bin]);

  const onClickSubmit = useCallback(() => {
    onSubmit(company, user);
  }, [company, user, onSubmit]);

  const partnerName = useMemo<string>(() => {
    let returnName = '';
    if (!!company) {
      returnName = company.name;
    }
    if (!!user) {
      returnName = fullName(user);
    }
    return returnName;
  }, [company, user]);

  const inputError = errorType !== null;

  return (
    <div className="bg-card rounded-lg p-6 shadow w-full max-w-2xl mx-auto flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Партнер</label>
            <input
              className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-base placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={partnerName}
              disabled
            />
          </div>
          <div>
            <label htmlFor="bin" className="block text-sm font-medium mb-1">
              БИН/ИИН
            </label>
            <input
              id="bin"
              inputMode="numeric"
              autoComplete="off"
              className={`w-full h-10 rounded-md border px-3 py-2 text-base placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                inputError ? 'border-red-500' : 'border-input'
              }`}
              placeholder="Укажите БИН/ИИН (12 цифр)"
              value={bin}
              onChange={onChangeBin}
              maxLength={BIN_IIN_LENGTH}
            />
            <span className="block min-h-[18px] text-xs mt-1 text-red-500">
              {errorType === 'self' &&
                'Вы не можете добавить себя в качестве партнера'}
              {errorType === 'not_found' && 'Партнер с таким БИН/ИИН не найден'}
            </span>
          </div>
        </div>
      </div>
      <div className="flex gap-4 justify-end">
        {!partnerFound ? (
          <Button
            onClick={onClickFind}
            disabled={pending || bin.length !== BIN_IIN_LENGTH}
          >
            {pending ? 'Поиск...' : 'Найти'}
          </Button>
        ) : (
          <Button onClick={onClickSubmit}>Продолжить</Button>
        )}
      </div>
    </div>
  );
}

export default memo(Step1);
